/**
 * Nexus LLM Service
 *
 * Given an item's context and the user's stated intent (plain language),
 * Nexus suggests the correct relationship type, direction, and rationale
 * from the canonical taxonomy.
 *
 * Runs through Peak LLM only — honors the Nexus agent's model; the workspace
 * prompt is an overlay on this advisor's contract through the instruction
 * stack (supabase/functions/_shared/relationshipCore.ts), never a replacement.
 * Throws {@link AgentLlmError} when Peak LLM is unavailable or misconfigured.
 */

import type { RelationshipEdgeType, DependencySubtype, ElaborationSubtype } from '../types/prioritization/itemRelationships';
import { AgentLlmError, callAgentLlm } from './llmClient';
import { buildRelationshipSystemPrompt } from '../../supabase/functions/_shared/relationshipCore.ts';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface NexusInput {
  /** Current item title */
  itemTitle: string;
  /** Current item description */
  itemDescription?: string;
  /** Current item category (e.g. 'problem', 'solution') */
  itemCategory?: string;
  /** User's plain-language statement of what they want to accomplish */
  userIntent: string;
  /** Existing edges on the item (for context) */
  existingEdges?: Array<{
    type: string;
    direction: string;
    otherItemTitle?: string;
  }>;
  /** Model to use */
  model?: string;
  /** The workspace's prompt overlay — it specializes the advisor's contract, never replaces it. */
  systemPrompt?: string | null;
}

export interface NexusSuggestion {
  /**
   * Suggested relationship type from the canonical taxonomy.
   * One of: cluster, package, dependency, prerequisite, aggregation, breakdown, elaboration, collection
   */
  suggestedType: RelationshipEdgeType | 'cluster' | 'package' | 'collection';
  /**
   * Subtype if applicable (e.g. 'hard' for dependency, 'solution_spectrum' for elaboration)
   */
  suggestedSubtype?: DependencySubtype | ElaborationSubtype | 'duplicate' | 'variant' | 'prioritize' | 'select' | 'pool' | 'curation';
  /**
   * 'directed' for dependency/prerequisite/aggregation/breakdown/elaboration;
   * 'undirected' for cluster/package/collection.
   */
  directionality: 'directed' | 'undirected';
  /**
   * For directed relationships: how does this item relate to the target?
   * 'source' = this item is the upstream/parent/causal item
   * 'target' = this item is the downstream/child/dependent item
   */
  thisItemRole?: 'source' | 'target';
  /**
   * Plain-language rationale explaining the suggestion.
   */
  rationale: string;
  /** Confidence in the suggestion */
  confidence: 'high' | 'medium' | 'low';
  /**
   * Actionable next step — tells the user exactly where to go and what to do
   * if they want to carry this out manually.
   */
  guidance: string;
  /**
   * If the user mentioned a specific other item by name, this is that name.
   * Can be used to search the workspace for a target.
   */
  mentionedTargetTitle?: string;
}

// ── System prompt ─────────────────────────────────────────────────────────────

const DEFAULT_SYSTEM_PROMPT = `You are Nexus, a relationship advisor for a strategic prioritization system.

Your job is to interpret a user's plain-language statement about what they want to accomplish
and suggest the correct relationship type from the system's canonical taxonomy.

## Relationship taxonomy

**Horizontal relationships** (peer-to-peer):
- **Cluster** (identity bond): items that are really the same thing — duplicates or variants. Undirected.
- **Package** (atomicity): items that must move together as a unit (subtypes: prioritize, select, prioritize_and_select). Undirected.
- **Dependency** (sequence/condition): one item must happen before another. Directed. Subtypes: informational, soft, hard.
- **Prerequisite** (enablement): this item must be resolved before others can proceed — the upstream counterpart to a dependency. Directed.

**Vertical relationships** (hierarchical):
- **Aggregation** (composition, bottom-up): lower-level items together constitute a higher-level whole.
- **Breakdown** (decomposition, top-down): a higher-level item consists of constituent lower-level parts.
- **Elaboration** (possibility spectrum): child items map how a parent need could be fulfilled, typically as a choose-one continuum from minimally sufficient through sufficient to ideal. Use solution_spectrum when sufficiency, ambition, altitude, or investment differs; use solution_variant for different approaches at a similar level. Unlike Competing, Elaboration has a parent need and is not a peer-level at-most-one policy.

**Curatorial**:
- **Collection** (curation): a named group of items for reference, reporting, or communication. Undirected.

## Output format

Respond ONLY with valid JSON matching this structure:
{
  "suggestedType": "<one of: related|dependency|prerequisite|aggregation|elaboration|breakdown> or for groups: <cluster|package|collection>",
  "suggestedSubtype": "<optional subtype or null>",
  "directionality": "<directed|undirected>",
  "thisItemRole": "<source|target|null>",
  "rationale": "<1–2 sentence explanation of why this relationship type fits>",
  "confidence": "<high|medium|low>",
  "guidance": "<one concrete sentence telling the user what to do next>",
  "mentionedTargetTitle": "<name of specific item mentioned by user, or null>"
}

Rules:
- thisItemRole is "source" when this item is the upstream/parent/enabling item, "target" when it is downstream/dependent/child
- For undirected relationships (cluster, package, collection), thisItemRole is null
- If confidence is low, say so honestly and explain ambiguity in the rationale
- mentionedTargetTitle should be the exact name the user mentioned, or null if they described a category rather than a specific item
- Never suggest creating both a Dependency and a Prerequisite for the same pair`;

// ── Prompt builder ────────────────────────────────────────────────────────────

export function buildPrompt(input: NexusInput): string {
  const parts: string[] = [];

  parts.push(`## Item being analyzed`);
  parts.push(`Title: ${input.itemTitle}`);
  if (input.itemDescription) {
    parts.push(`Description: ${input.itemDescription}`);
  }
  if (input.itemCategory) {
    parts.push(`Category: ${input.itemCategory}`);
  }

  if (input.existingEdges && input.existingEdges.length > 0) {
    parts.push(`\n## Existing relationships on this item`);
    input.existingEdges.forEach((e) => {
      const target = e.otherItemTitle ? ` → "${e.otherItemTitle}"` : '';
      parts.push(`- ${e.type} (${e.direction})${target}`);
    });
  }

  parts.push(`\n## What the user wants to accomplish`);
  parts.push(input.userIntent);

  parts.push(`\n## Task`);
  parts.push(`Analyze the user's intent and suggest the best relationship type from the taxonomy. Return JSON only.`);

  return parts.join('\n');
}

// ── JSON parser ───────────────────────────────────────────────────────────────

export function parseSuggestion(raw: string): NexusSuggestion {
  try {
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) {
      throw new AgentLlmError('Nexus agent could not parse the LLM response.');
    }
    const obj = JSON.parse(match[0]) as Record<string, unknown>;

    if (typeof obj.suggestedType !== 'string' || typeof obj.rationale !== 'string') {
      throw new AgentLlmError('Nexus agent could not parse the LLM response.');
    }

    return {
      suggestedType:       obj.suggestedType as NexusSuggestion['suggestedType'],
      suggestedSubtype:    (obj.suggestedSubtype as NexusSuggestion['suggestedSubtype']) ?? undefined,
      directionality:      (obj.directionality as 'directed' | 'undirected') ?? 'undirected',
      thisItemRole:        (obj.thisItemRole as 'source' | 'target') ?? undefined,
      rationale:           obj.rationale as string,
      confidence:          (obj.confidence as 'high' | 'medium' | 'low') ?? 'medium',
      guidance:            typeof obj.guidance === 'string' ? obj.guidance : '',
      mentionedTargetTitle: typeof obj.mentionedTargetTitle === 'string' && obj.mentionedTargetTitle
        ? obj.mentionedTargetTitle
        : undefined,
    };
  } catch (err) {
    if (err instanceof AgentLlmError) throw err;
    throw new AgentLlmError('Nexus agent could not parse the LLM response.');
  }
}

// ── Public entry point ────────────────────────────────────────────────────────

export async function runNexusAgent(input: NexusInput): Promise<NexusSuggestion> {
  if (!input.userIntent.trim()) {
    throw new AgentLlmError('Describe what you want to accomplish before asking Nexus.');
  }

  const model = input.model ?? DEFAULT_MODEL;
  const { systemPrompt } = buildRelationshipSystemPrompt({ overlay: input.systemPrompt ?? null, appendix: DEFAULT_SYSTEM_PROMPT });
  const userPrompt = buildPrompt(input);

  const content = await callAgentLlm({
    userPrompt,
    systemPrompt,
    model,
    conversationId: `nexus-${Date.now()}`,
    maxTokens: 800,
    temperature: 0.3,
    timeoutMs: 30_000,
  });

  return parseSuggestion(content);
}
